import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, X, Play, Presentation, Maximize2, ExternalLink, CalendarPlus, Route as RouteIcon, ClipboardList, StickyNote } from "lucide-react";

const NAVY = "#13216A";
const ROSE = "#D1005F";
const ytId = (u = "") => (u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/) || [])[1];
const vimeoId = (u = "") => (u.match(/vimeo\.com\/(?:video\/)?(\d+)/) || [])[1];
export const embedOf = (u) => (ytId(u) ? `https://www.youtube-nocookie.com/embed/${ytId(u)}?autoplay=1&rel=0` : vimeoId(u) ? `https://player.vimeo.com/video/${vimeoId(u)}?autoplay=1` : null);
const thumbOf = (u) => (ytId(u) ? `https://img.youtube.com/vi/${ytId(u)}/hqdefault.jpg` : null);

function Heading({ s, small }) {
  return (
    <div className={small ? "mb-4" : "mb-6"}>
      <h2 className={`font-display font-bold tracking-tight ${small ? "text-xl sm:text-2xl" : "text-2xl sm:text-4xl"}`} style={{ color: NAVY }} data-testid="ao-slide-title">{s.title}</h2>
      {s.subtitle && <p className="mt-2 text-base sm:text-lg text-slate-600 max-w-3xl" data-testid="ao-slide-subtitle">{s.subtitle}</p>}
    </div>
  );
}

function SlideImage({ s }) {
  const [open, setOpen] = useState(false);
  if (!s.image_url) {
    return <div className="aspect-[16/9] w-full rounded-2xl border-2 border-dashed border-pink-200 bg-pink-50/60 flex items-center justify-center text-sm text-pink-700" data-testid="ao-image-placeholder">Slide image coming soon</div>;
  }
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="group relative block w-full overflow-hidden rounded-2xl ring-1 ring-slate-200 bg-white shadow-sm" data-testid="ao-slide-image-btn" aria-label={`Enlarge ${s.title}`}>
        <img src={s.image_url} alt={s.image_alt || s.title} className="w-full h-auto object-contain" loading="lazy" data-testid="ao-slide-image" />
        <span className="absolute right-3 top-3 rounded-full bg-white/90 p-2 text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity"><Maximize2 className="w-4 h-4" /></span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[96vw] w-[96vw] p-2 bg-white" data-testid="ao-image-zoom">
          <DialogTitle className="sr-only">{s.title}</DialogTitle>
          <img src={s.image_url} alt={s.image_alt || s.title} className="w-full h-auto max-h-[88vh] object-contain" />
        </DialogContent>
      </Dialog>
    </>
  );
}

const Body = ({ html }) => (html ? <div className="tr-page ao-body mt-5" dangerouslySetInnerHTML={{ __html: html }} data-testid="ao-slide-body" /> : null);

function Cta({ s, onAction }) {
  if (!s.cta_label || !s.cta_url) return null;
  return <Button onClick={() => onAction(s.cta_url)} className="mt-6 rounded-full px-6 text-white" style={{ background: ROSE }} data-testid="ao-slide-cta">{s.cta_label}<ChevronRight className="w-4 h-4 ml-1" /></Button>;
}

function PathCards({ cards, selectable }) {
  const [sel, setSel] = useState(null);
  return (
    <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="ao-path-cards">
      {cards.map((c, i) => {
        const on = sel === i;
        const Tag = selectable ? "button" : "div";
        return (
          <Tag key={i} type={selectable ? "button" : undefined} onClick={selectable ? () => setSel(on ? null : i) : undefined} aria-pressed={selectable ? on : undefined}
            className={`text-left rounded-2xl p-6 transition-[transform,box-shadow,background-color] duration-200 ${on ? "text-white shadow-xl -translate-y-1" : "bg-white ring-1 ring-slate-200 hover:-translate-y-1 hover:shadow-lg"}`}
            style={on ? { background: NAVY } : undefined} data-testid={`ao-path-card-${c.pathway || i}`}>
            <span className="text-xs font-bold tracking-[0.2em]" style={{ color: on ? "#FFB3D4" : ROSE }}>{c.tag}</span>
            <p className={`mt-3 font-display text-lg font-bold leading-snug ${on ? "text-white" : ""}`} style={on ? undefined : { color: NAVY }}>{c.title}</p>
            <p className={`mt-2 text-sm leading-relaxed ${on ? "text-white/85" : "text-slate-600"}`}>{c.text}</p>
          </Tag>
        );
      })}
    </div>
  );
}

function VideoCard({ v }) {
  const [open, setOpen] = useState(false);
  const thumb = thumbOf(v.url);
  const emb = embedOf(v.url);
  return (
    <>
      <button type="button" onClick={() => (emb ? setOpen(true) : window.open(v.url, "_blank", "noopener"))} className="group text-left rounded-2xl overflow-hidden bg-slate-900 ring-1 ring-slate-200 shadow-sm hover:shadow-xl transition-shadow" data-testid={`ao-video-${ytId(v.url) || "card"}`}>
        <div className="relative aspect-video">
          {thumb ? <img src={thumb} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" loading="lazy" /> : <div className="absolute inset-0" style={{ background: NAVY }} />}
          <span className="absolute inset-0 flex items-center justify-center"><span className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform" style={{ background: ROSE }}><Play className="w-7 h-7 text-white ml-1" fill="white" /></span></span>
        </div>
        <div className="bg-white px-5 py-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: ROSE }}>Agency Owner Story</p>
          <p className="font-display text-lg font-bold" style={{ color: NAVY }}>{v.title}</p>
        </div>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black" data-testid="ao-video-modal">
          <DialogTitle className="sr-only">{v.title}</DialogTitle>
          {open && <iframe title={v.title} src={emb} className="w-full aspect-video" allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowFullScreen data-testid="ao-video-iframe" />}
          <div className="bg-white px-4 py-3 flex items-center justify-between text-sm">
            <span className="font-semibold" style={{ color: NAVY }}>{v.title}</span>
            <span className="flex items-center gap-4"><a href={v.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900" data-testid="ao-video-youtube-link">Watch on YouTube<ExternalLink className="w-3.5 h-3.5" /></a>
              <Button size="sm" variant="outline" onClick={() => setOpen(false)} data-testid="ao-video-close">Close</Button></span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function NextSteps({ s, deck, onAction }) {
  const acts = deck.next_actions || [];
  return (
    <>
      <Body html={s.body} />
      {acts.length > 0 && (
        <div className="mt-6" data-testid="ao-next-actions">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">Your current next actions</p>
          <ul className="space-y-2">{acts.map((a) => (
            <li key={a.id}><button type="button" onClick={() => onAction(`#task:${a.id}`)} className="w-full text-left flex items-center gap-3 rounded-xl bg-white ring-1 ring-slate-200 px-4 py-3 hover:ring-pink-300" data-testid={`ao-next-action-${a.id}`}>
              <ClipboardList className="w-4 h-4 shrink-0" style={{ color: ROSE }} /><span className="text-sm font-medium text-slate-800 flex-1">{a.title}</span>
              {a.due_date && <span className="text-xs text-slate-500">Due {new Date(a.due_date).toLocaleDateString()}</span>}
            </button></li>))}
          </ul>
        </div>
      )}
      <div className="mt-7 flex flex-wrap gap-3">
        <Button onClick={() => onAction("#journey")} className="rounded-full px-6 text-white" style={{ background: NAVY }} data-testid="ao-view-journey"><RouteIcon className="w-4 h-4 mr-1.5" />View My Journey</Button>
        <Button onClick={() => onAction("#schedule")} variant="outline" className="rounded-full px-6" data-testid="ao-schedule"><CalendarPlus className="w-4 h-4 mr-1.5" />Schedule a Meeting</Button>
      </div>
    </>
  );
}

export function SlideView({ s, deck, onAction, small }) {
  const t = s.type;
  return (
    <div data-testid={`ao-slide-${s.id}`} data-slide-type={t}>
      {!["image"].includes(t) || !s.image_url ? <Heading s={s} small={small} /> : <h2 className="sr-only">{s.title}</h2>}
      {["image", "hybrid"].includes(t) && <SlideImage s={s} />}
      {["four_paths", "path_picker"].includes(t) && <PathCards cards={s.cards || []} selectable={t === "path_picker"} />}
      {["stories", "video"].includes(t) && <div className="grid md:grid-cols-2 gap-5" data-testid="ao-videos">{(s.videos || []).map((v, i) => <VideoCard key={i} v={v} />)}</div>}
      {t === "next_steps" ? <NextSteps s={s} deck={deck} onAction={onAction} /> : <Body html={s.body} />}
      {t === "path_picker" && <p className="mt-4 text-xs text-slate-500">Selecting a path here is for information only and does not change your program.</p>}
      {t === "path_picker" && <Button onClick={() => onAction("#schedule")} className="mt-5 rounded-full px-6 text-white" style={{ background: ROSE }} data-testid="ao-discuss-options">Discuss My Options</Button>}
      <Cta s={s} onAction={onAction} />
    </div>
  );
}

function Presenter({ deck, start, onExit, onAction, notes }) {
  const slides = deck.slides;
  const [i, setI] = useState(start);
  const [showNotes, setShowNotes] = useState(false);
  const touch = useRef(null);
  const stage = useRef(null);
  const go = (n) => setI((x) => Math.max(0, Math.min(slides.length - 1, x + n)));
  useEffect(() => {
    const k = (e) => {
      if (document.querySelector('[role="dialog"]')) return;
      if (e.key === "ArrowRight") go(1); else if (e.key === "ArrowLeft") go(-1); else if (e.key === "Escape") onExit();
    };
    window.addEventListener("keydown", k);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", k); document.body.style.overflow = ""; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { stage.current?.scrollTo(0, 0); }, [i]);
  const s = slides[i];
  const act = (u) => {
    const k = u === "#stories" ? slides.findIndex((x) => x.type === "stories") : -1;
    if (k >= 0) setI(k); else if (onAction(u) !== false) onExit();
  };
  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col bg-[#0B1240]" data-testid="ao-presenter"
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => { const d = e.changedTouches[0].clientX - (touch.current ?? 0); if (Math.abs(d) > 60) go(d < 0 ? 1 : -1); }}>
      <div className="h-14 shrink-0 flex items-center justify-between px-4 sm:px-6 text-white">
        <p className="text-sm font-semibold truncate"><span className="text-pink-300 mr-2" data-testid="ao-presenter-count">{i + 1} / {slides.length}</span>{s?.title}</p>
        <div className="flex items-center gap-2">
          {notes && s?.presenter_notes && <Button size="sm" variant="ghost" onClick={() => setShowNotes(!showNotes)} className="text-white hover:bg-white/10" data-testid="ao-presenter-notes-toggle"><StickyNote className="w-4 h-4 mr-1" />Notes</Button>}
          <Button size="sm" variant="ghost" onClick={onExit} className="text-white hover:bg-white/10" data-testid="ao-presenter-exit"><X className="w-4 h-4 mr-1" />Exit Presentation</Button>
        </div>
      </div>
      <div ref={stage} className="flex-1 overflow-y-auto px-3 sm:px-8 pb-4">
        <div className="mx-auto max-w-6xl rounded-3xl bg-[#FFF5FA] p-5 sm:p-10 shadow-2xl ao-enter" key={s?.id}>
          {s && <SlideView s={s} deck={deck} onAction={act} />}
          {i === slides.length - 1 && deck.disclaimer && <p className="mt-8 border-t border-pink-100 pt-4 text-[11px] leading-relaxed text-slate-500" data-testid="ao-presenter-disclaimer">{deck.disclaimer}</p>}
        </div>
        {showNotes && s?.presenter_notes && <div className="mx-auto max-w-6xl mt-3 rounded-2xl bg-amber-50 p-4 text-sm text-amber-900 whitespace-pre-wrap" data-testid="ao-presenter-notes"><b>Internal presenter notes — not visible to Candidates.</b><br />{s.presenter_notes}</div>}
      </div>
      <div className="h-16 shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6">
        <Button onClick={() => go(-1)} disabled={i === 0} variant="outline" className="rounded-full bg-white/10 text-white border-white/20 hover:bg-white/20 hover:text-white" data-testid="ao-presenter-prev"><ChevronLeft className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Previous</span></Button>
        <div className="flex gap-1.5 overflow-x-auto max-w-[55vw]">{slides.map((x, n) => <button key={x.id} onClick={() => setI(n)} aria-label={`Go to slide ${n + 1}`} className={`h-2 rounded-full transition-[width,background-color] ${n === i ? "w-6 bg-pink-400" : "w-2 bg-white/30 hover:bg-white/60"}`} data-testid={`ao-presenter-dot-${n}`} />)}</div>
        <Button onClick={() => go(1)} disabled={i === slides.length - 1} className="rounded-full text-white" style={{ background: ROSE }} data-testid="ao-presenter-next"><span className="hidden sm:inline">Next</span><ChevronRight className="w-4 h-4 sm:ml-1" /></Button>
      </div>
    </div>,
    document.body
  );
}

function ChapterCard({ s, n, onOpen }) {
  return (
    <button type="button" onClick={onOpen} className="group text-left rounded-2xl overflow-hidden bg-white ring-1 ring-slate-200 hover:ring-pink-300 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow]" data-testid={`ao-chapter-${n}`}>
      <div className="aspect-[16/9] bg-[#FDE7F1] overflow-hidden relative">
        {s.image_url ? <img src={s.image_url} alt="" className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
          : <div className="absolute inset-0 flex items-end p-4" style={{ background: `linear-gradient(135deg, ${NAVY}, #2B3A8F)` }}><span className="font-display text-white text-lg font-bold leading-tight">{s.title}</span></div>}
      </div>
      <div className="px-4 py-3">
        <p className="text-[11px] font-bold tracking-[0.18em]" style={{ color: ROSE }}>CHAPTER {n + 1}</p>
        <p className="font-semibold text-slate-900 leading-snug">{s.title}</p>
      </div>
    </button>
  );
}

export function OwnershipDeck({ deck, onAction, notes = false, autoStart = false, onExitPresenter, banner }) {
  const [start, setStart] = useState(autoStart ? 0 : null);
  const slides = deck.slides || [];
  const exit = () => { setStart(null); onExitPresenter?.(); };
  const handle = (u) => {
    const k = u === "#stories" ? slides.findIndex((x) => x.type === "stories") : -1;
    if (k >= 0) setStart(k); else onAction(u);
  };
  return (
    <div data-testid="ao-deck">
      {banner}
      <section className="relative overflow-hidden rounded-3xl p-7 sm:p-12 text-white" style={{ background: `linear-gradient(120deg, ${NAVY} 0%, #1C2E86 60%, #3A1F6E 100%)` }} data-testid="ao-hero">
        <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-pink-500/20 blur-3xl" />
        <p className="text-xs font-bold tracking-[0.25em] text-pink-300">FARMERS AGENCY OWNERSHIP</p>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight">Agency Ownership</h1>
        {deck.intro && <div className="mt-4 max-w-2xl text-white/85 text-base sm:text-lg [&_p]:mt-0" dangerouslySetInnerHTML={{ __html: deck.intro }} data-testid="ao-intro" />}
        <Button onClick={() => setStart(0)} disabled={!slides.length} className="mt-7 rounded-full px-7 h-12 text-base text-white shadow-lg" style={{ background: ROSE }} data-testid="ao-start-presentation"><Presentation className="w-5 h-5 mr-2" />Start Presentation</Button>
      </section>
      {slides.length === 0 ? <p className="mt-8 text-slate-500" data-testid="ao-empty">The presentation is being prepared. Please check back soon.</p> : (
        <>
          <h2 className="mt-10 mb-4 font-display text-lg font-semibold text-slate-900">Chapters</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ao-chapters">{slides.map((s, n) => <ChapterCard key={s.id} s={s} n={n} onOpen={() => setStart(n)} />)}</div>
          <h2 className="mt-12 mb-4 font-display text-lg font-semibold text-slate-900">Read at your own pace</h2>
          <div className="space-y-6" data-testid="ao-scroll">{slides.map((s) => <section key={s.id} className="rounded-3xl bg-[#FFF5FA] ring-1 ring-pink-100 p-5 sm:p-9"><SlideView s={s} deck={deck} onAction={handle} small /></section>)}</div>
        </>
      )}
      {deck.disclaimer && <p className="mt-8 text-[11px] leading-relaxed text-slate-500" data-testid="ao-disclaimer">{deck.disclaimer}</p>}
      {start !== null && slides.length > 0 && <Presenter deck={deck} start={start} onExit={exit} onAction={onAction} notes={notes} />}
    </div>
  );
}
